// Part of the landing page (split out of pages/Landing.js, 2026-09-24).
import React, { useState } from 'react';
import { PALETTE, FAQ_ITEMS } from './content';

// ── PriceChart ─────────────────────────────────────────────────────────────
function PriceChart({ width = 540, height = 200, points, target }) {
  const pad = { l: 36, r: 16, t: 16, b: 28 };
  const min = Math.min(...points, target) - 5;
  const max = Math.max(...points, target) + 5;
  const range = max - min;
  const xStep = (width - pad.l - pad.r) / (points.length - 1);
  const yFor = (v) => pad.t + (1 - (v - min) / range) * (height - pad.t - pad.b);
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${pad.l + i * xStep},${yFor(p)}`).join(' ');
  const dArea = `${d} L${pad.l + (points.length - 1) * xStep},${height - pad.b} L${pad.l},${height - pad.b} Z`;
  const ty = yFor(target);
  const last = points[points.length - 1];
  const lastX = pad.l + (points.length - 1) * xStep;
  const lastY = yFor(last);
  return (
    <svg width={width} height={height} style={{ display: 'block', maxWidth: '100%' }}>
      <defs>
        <linearGradient id="chartArea" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={PALETTE.orange} stopOpacity="0.18" />
          <stop offset="100%" stopColor={PALETTE.orange} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
        const y = pad.t + t * (height - pad.t - pad.b);
        const v = Math.round(max - t * range);
        return (
          <g key={i}>
            <line x1={pad.l} x2={width - pad.r} y1={y} y2={y} stroke={PALETTE.line} strokeWidth="1" />
            <text x={pad.l - 8} y={y + 3} fontSize="10" fill={PALETTE.muted} textAnchor="end" fontFamily="'JetBrains Mono', monospace">${v}</text>
          </g>
        );
      })}
      {['Apr 1', 'Apr 8', 'Apr 15', 'Apr 22', 'Apr 29'].map((lbl, i) => (
        <text key={i} x={pad.l + (i / 4) * (width - pad.l - pad.r)} y={height - 10} fontSize="10" fill={PALETTE.muted} textAnchor="middle" fontFamily="'JetBrains Mono', monospace">{lbl}</text>
      ))}
      <path d={dArea} fill="url(#chartArea)" />
      <path d={d} fill="none" stroke={PALETTE.orange} strokeWidth="2" />
      <line x1={pad.l} y1={ty} x2={width - pad.r} y2={ty} stroke={PALETTE.navy} strokeDasharray="4 4" strokeWidth="1.2" />
      <text x={width - pad.r - 4} y={ty - 6} fontSize="10" fill={PALETTE.navy} textAnchor="end" fontFamily="'JetBrains Mono', monospace" fontWeight="700">target ${target}</text>
      <circle cx={lastX} cy={lastY} r="5" fill="#fff" stroke={PALETTE.orange} strokeWidth="2" />
      <circle cx={lastX} cy={lastY} r="2" fill={PALETTE.orange} />
    </svg>
  );
}

// ── EmailPreview (hero mockup alt state) ───────────────────────────────────
function EmailPreview() {
  return (
    <div className="l-email-preview">
      <div className="l-email-chrome">
        <span className="l-mono" style={{ fontSize: 10, color: PALETTE.muted, letterSpacing: 1 }}>📧 INBOX · ALL MAIL</span>
        <span className="l-mono" style={{ fontSize: 10, color: PALETTE.muted }}>2 MIN AGO</span>
      </div>
      <div className="l-email-header">
        <div className="l-mono" style={{ fontSize: 11, color: PALETTE.muted }}>
          FROM <strong style={{ color: PALETTE.navy }}>alerts@pricehuntify.com</strong>
          <span style={{ marginLeft: 14 }}>TO you</span>
        </div>
        <div className="l-email-subject">
          Sony WH-1000XM5 hit <span style={{ color: PALETTE.orange }}>$155.00</span>{' '}
          <span style={{ color: PALETTE.green }}>↓ 22%</span>
        </div>
        <div style={{ fontSize: 12, color: PALETTE.muted, marginTop: 4 }}>
          You set target $160.00 on Apr 12 · It went lower.
        </div>
      </div>
      <div className="l-email-body">
        <div className="l-mono" style={{ fontSize: 10, color: PALETTE.muted, letterSpacing: 1.5, marginBottom: 4 }}>CURRENT PRICE</div>
        <div className="l-email-price">$155<span style={{ fontSize: 22 }}>.00</span></div>
        <div style={{ fontSize: 12, color: PALETTE.navy, marginTop: 4 }}>
          That's <strong style={{ color: PALETTE.green }}>$44 below</strong> your target.
        </div>
        <div className="l-email-cta">BUY NOW AT AMAZON →</div>
      </div>
      <div className="l-email-foot l-mono">
        ↳ This is the only email we'll send about this product.<br />
        <span style={{ color: PALETTE.orange }}>Stop tracking</span> · Adjust target
      </div>
    </div>
  );
}

// ── FlowStep ────────────────────────────────────────────────────────────────
function FlowStep({ n, title, desc }) {
  return (
    <div className="l-flow-step">
      <div className="l-flow-step-num">STEP {n}</div>
      <div className="l-flow-step-title">{title}</div>
      <div className="l-flow-step-desc">{desc}</div>
    </div>
  );
}

// ── FAQ accordion ───────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="l-faq" id="faq">
      <div className="l-faq-header">
        <div className="l-section-label">FAIR QUESTIONS</div>
        <h2>The stuff every visitor asks</h2>
      </div>
      <div className="l-faq-list">
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className={`l-faq-item${isOpen ? ' l-faq-item--open' : ''}`}>
              <button
                className="l-faq-q"
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
              >
                <span>{item.q}</span>
                <span className="l-faq-toggle">{isOpen ? '−' : '+'}</span>
              </button>
              {isOpen && (
                <div className="l-faq-a">
                  {item.a}
                  {item.link && <a href={item.link.href} style={{ color: 'var(--l-orange)', textDecoration: 'underline' }}>{item.link.label}</a>}
                  {item.aSuffix}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export { PriceChart, EmailPreview, FlowStep, FAQ };
